import { Grid, Stack, Typography} from '@mui/material'
import { GItem, GItemContainer, MiddleGItem } from '../components/common/grids'
import { SubTitle } from '../components/common/subTitle'
import { AttentionLabel } from '../components/common/attentionLabel'
import { ErrorMessageCard } from '../components/common/errorMessageCard'
import { BottomButtonGroup } from '../components/common/bottomButtonGroup'
import { ButtonOutlinedWarningL } from '../components/common/buttons/buttonOutlinedWarningL'
import { BackButton } from '../components/common/buttons/backButton'
import { Form } from '../components/common/inputs/form'
import { RadioButtonGroup } from '../components/common/inputs/radioButtonGroup'
import { TextBox } from '../components/common/inputs/textBox'
import { TextBoxAfterText } from '../components/common/inputs/textBoxAfterText'
import { FlexibleCheckBoxesUnit } from '../components/common/memberModifyChild/flexibleCheckBoxesUnit'
import { translate } from '../../i18n'
import { yesNo } from '../../containers/common/constant/classification'
import { GetCategoryMstDto } from '../../dataAccess/webApi/dto/categoryMstDto'
import { useAction } from '../../containers/memberModifyChildEating/memberModifyChildEatingService'

export const MemberModifyChildEating = () => {
  const {
    CATEGORY_KEY,
    formMethods,
    categoryMst,
    onSubmit,
    childrenResultCode,
  } = useAction()
  
  const targetCategories = categoryMst?.filter((value) => CATEGORY_KEY.includes(value.category))
  
  return (
    <Form formMethods={formMethods} onSubmit={onSubmit}>
      <Stack spacing={2}>
        <Grid container>
          <Grid item md={3}></Grid>
          <Grid item xs={12} md={6}>
            <Stack spacing={2}>
              {targetCategories != null && targetCategories.length > 0 ?
                (<>
                  <SubTitle title={'食事'} />
                  {
                    targetCategories.map((mst) => {
                      switch(mst?.category){
                        case "eatingStyle":
                          return (
                          <GItemContainer xs={12} rowSpacing={1}>
                           <MiddleGItem xs={5} sm={2}>{mst.name}</MiddleGItem>
                            <GItem xs={7} sm={10}>
                            <FlexibleCheckBoxesUnit
                              categoryMst = {mst}
                              indentOff = {true}
                            />
                            </GItem>
                          </GItemContainer>
                          )
                        default:
                          return (
                            <InputForm categoryMst={mst} key={mst.category} />
                          )
                      }
                    })
                  }
                </>)
                :
                (<>
                  {translate('memberModifyChildEating.error.noCategoryMst')}
                </>)
              }
            </Stack>
          </Grid>
        </Grid>
        {!!childrenResultCode && (
          <ErrorMessageCard
            messages={[translate(`memberModifyConfirmationChild.error.childrenResultCode${childrenResultCode}`)]}
          />
        )}
        <BottomButtonGroup>
          <ButtonOutlinedWarningL fullWidth type="submit">
            {translate('memberModifyChildEating.button.decision')}
          </ButtonOutlinedWarningL>
          <BackButton />
        </BottomButtonGroup>
      </Stack>
    </Form>
  )
}

interface InputFormProps {
  categoryMst: GetCategoryMstDto
}

/** 各項目の入力フォーム */
const InputForm = (props: InputFormProps) => {
  const { categoryMst } = props

  const render = () => {

    switch(categoryMst.category) {
      case "babyFoodStartEnd":
        return (
          <GItemContainer xs={12} rowSpacing={1}>
            <MiddleGItem xs={5} sm={2}>
              {categoryMst.required === yesNo.yes && (
                <AttentionLabel>*</AttentionLabel>
              )}
              {categoryMst.name}
            </MiddleGItem>
            <GItem xs={7} sm={10}>
              <Grid container alignItems="center" spacing={1}>
                <Grid item>
                  <TextBoxAfterText
                    label={categoryMst.name}
                    name={'eating.babyFoodStart'}
                    maxLength={categoryMst.maxLength}
                    required={categoryMst.required === yesNo.yes}
                    afterText={translate('memberModifyChildEating.label.month')}
                  />
                </Grid>
                <Grid item>
                  <Typography component={'span'} sx={{ whiteSpace: 'nowrap' }}>
                    ～
                  </Typography>
                </Grid>
                <Grid item>
                  <TextBoxAfterText
                    label={categoryMst.name}
                    name={'eating.babyFoodEnd'}
                    maxLength={categoryMst.maxLength}
                    required={categoryMst.required === yesNo.yes}
                    afterText={translate('memberModifyChildEating.label.month')}
                  />
                </Grid>
              </Grid>
            </GItem>
          </GItemContainer>
        )

      case "howToNurse":
      case "babyFoodStage":
      case "babyFoodShape":
      case "babyFoodRice":
      case "milk":
        if (categoryMst.options == null || categoryMst.options.length === 0) return null
        
        const labelData = categoryMst.options.map(op => ({ label: op.optionName, value: op.code }))
        return (
          <GItemContainer xs={12} rowSpacing={1}>
            <MiddleGItem xs={5} sm={2}>
              {categoryMst.required === yesNo.yes && (
                <AttentionLabel>*</AttentionLabel>
              )}
              {categoryMst.name}
            </MiddleGItem>
            <GItem xs={7} sm={10}>
              <RadioButtonGroup
                label={categoryMst.altCategoryName ?? categoryMst.name}
                name={`eating.${categoryMst.category}`}
                buttonValueLabels={labelData}
                required={categoryMst.required === yesNo.yes}
                row
              />
            </GItem>
          </GItemContainer>
        )
  
      case "howToNurseNote":
      case "favoriteFood":
      case "hatedFood": 
      case "allergyFreeFood":
        return (
          <GItemContainer xs={12} rowSpacing={1}>
            <MiddleGItem xs={5} sm={2}>
              <GItemContainer xs={12}>
                <GItem xs={12}>
                  <div>
                    {categoryMst.required === yesNo.yes && (
                      <AttentionLabel>*</AttentionLabel>
                    )}
                    {categoryMst.name}
                  </div>
                </GItem>
              </GItemContainer>
            </MiddleGItem>
            <GItem xs={7} sm={10}>
              <TextBox
                label={categoryMst.name}
                name={`eating.${categoryMst.category}`}
                maxLength={categoryMst.maxLength}
                rows={1}
                required={categoryMst.required === yesNo.yes}
                multiline
                fullWidth
              />
            </GItem>
          </GItemContainer>
        )
      
      case "eatingNote":
        return (
          <GItemContainer xs={12} rowSpacing={1}>
            <MiddleGItem xs={5} sm={2}>
              <GItemContainer xs={12}>
                <GItem xs={12}>
                  <div>
                    {categoryMst.required === yesNo.yes && (
                      <AttentionLabel>*</AttentionLabel>
                    )}
                    {categoryMst.name}
                  </div>
                </GItem>
              </GItemContainer>
            </MiddleGItem>
            <GItem xs={7} sm={10}>
              <TextBox
                label={categoryMst.name}
                name={`eating.${categoryMst.category}`}
                maxLength={categoryMst.maxLength}
                rows={4}
                required={categoryMst.required === yesNo.yes}
                multiline
                fullWidth
              />
            </GItem>
          </GItemContainer>
        )
        
      default:
        return null
    }
  }
  
  return (render())
}
